import { api, tokens } from '../../lib/api';
import { paraYaz, type Para } from '../kasa/ortak';

/**
 * ÇEK / SENET · KREDİ · NAKİT AKIŞI — ekranların ortak tipleri ve küçük yardımcıları.
 * Kurallar sunucuda (`apps/api/src/cek.ts`, `kredi.ts`, `nakit-akis.ts`); burası yalnız gösterir.
 * Durum makinesi de sunucudan gelir: her çekin `gecisler` alanı o anda geçerli işlemleri taşır.
 */
export type CekTur = 'CEK' | 'SENET';
export type CekYon = 'ALINAN' | 'VERILEN';
export type CekDurum = 'PORTFOY' | 'BANKADA' | 'TEMINATTA' | 'TAHSIL' | 'CIRO' | 'KARSILIKSIZ' | 'IADE' | 'BEKLIYOR' | 'ODENDI';

export type Cek = {
  id: number; tur: CekTur; yon: CekYon; no: string; bankaAdi: string | null; sube: string | null; hesapNo: string | null; kesideci: string | null;
  customerId: number | null; supplierId: number | null; ciroSupplierId: number | null; cariAd: string | null; ciroAd: string | null;
  duzenlemeTarihi: string | null; vadeTarihi: string; tutarKurus: number; para: Para; durum: CekDurum; durumAd: string; resmi: boolean;
  hesapId: number | null; hesapAd: string | null; kendiHesapId: number | null; kendiHesapAd: string | null;
  orderId: number | null; orderNo: string | null; satisFaturaId: number | null; alisFaturaId: number | null;
  not: string | null; gorsel: string | null; nakitHareketId: number | null; kalanGun: number; bekliyor: boolean;
  gecisler: CekDurum[]; oncekiDurum: CekDurum | null; iptal: boolean; iptalNeden: string | null; olusturan: string | null; createdAt: string; updatedAt: string;
};
export type CekHareketi = {
  id: number; oncekiDurum: CekDurum | null; yeniDurum: CekDurum; oncekiAd: string | null; yeniAd: string; tarih: string;
  hesapAd: string | null; tedarikciAd: string | null; nakit: 'YAZILDI' | 'IPTAL' | null; not: string | null; kim: string | null; createdAt: string;
};
export type CekOzetSatiri = {
  para: Para; portfoyKurus: number; portfoyAdet: number; bankadaKurus: number; bankadaAdet: number; teminattaKurus: number; teminattaAdet: number;
  buAyGirisKurus: number; buAyGirisAdet: number; gecmisGirisKurus: number; gecmisGirisAdet: number; karsiliksizKurus: number; karsiliksizAdet: number;
  odenmemisKurus: number; odenmemisAdet: number; buAyCikisKurus: number; buAyCikisAdet: number; gecmisCikisKurus: number; gecmisCikisAdet: number;
};
export type CekOzet = { ozet: CekOzetSatiri[]; bugun: string; gayriGorur: boolean; uyariGunleri: number[]; durumAd: Record<CekDurum, string> };
export type CekListe = { satirlar: Cek[]; toplam: number; sayfa: number; boyut: number; toplamlar: { para: Para; kurus: number; adet: number }[] };
export type Belge = { id: number; ad: string; cariAd: string | null; cariId: number | null; kalanKurus: number };
export type HesapSecenek = { id: number; ad: string; tur: 'KASA' | 'BANKA' | 'EMANET'; resmi: boolean; para: Para; bakiyeKurus: number };
export type CekSecenekler = {
  musteriler: { id: number; ad: string; alt: string | null }[]; tedarikciler: { id: number; ad: string }[]; bankalar: string[];
  hesaplar: HesapSecenek[]; siparisler: Belge[]; satisFaturalari: Belge[]; alisFaturalari: Belge[]; kasaYazar: boolean;
};

export const TUR_AD: Record<CekTur, string> = { CEK: 'Çek', SENET: 'Senet' };
export const DURUM_KISA: Record<CekDurum, string> = {
  PORTFOY: 'Portföyde', BANKADA: 'Bankada', TEMINATTA: 'Teminatta', TAHSIL: 'Tahsil edildi', CIRO: 'Ciro edildi',
  KARSILIKSIZ: 'Karşılıksız', IADE: 'İade edildi', BEKLIYOR: 'Bekliyor', ODENDI: 'Ödendi',
};
/** Durum rozetinin tonu (kütüphanedeki rozet sınıfı). */
export const DURUM_ROZET: Record<CekDurum, string> = {
  PORTFOY: 'ccd-badge-info', BANKADA: 'ccd-badge-accent', TEMINATTA: 'ccd-badge-warning', TAHSIL: 'ccd-badge-success', CIRO: '',
  KARSILIKSIZ: 'ccd-badge-danger', IADE: '', BEKLIYOR: 'ccd-badge-warning', ODENDI: 'ccd-badge-success',
};

/** İşlem düğmesinin adı: hangi durumdan hangisine geçildiğine göre (geri almalar ayrı adlanır). */
export function islemAdi(onceki: CekDurum, yeni: CekDurum, yon: CekYon): string {
  if (yeni === 'TAHSIL') return 'Tahsil edildi';
  if (yeni === 'ODENDI') return 'Ödendi';
  if (yeni === 'CIRO') return 'Ciro et';
  if (yeni === 'KARSILIKSIZ') return 'Karşılıksız çıktı';
  if (yeni === 'IADE') return yon === 'ALINAN' ? 'Müşteriye iade et' : 'İade / iptal edildi';
  if (yeni === 'TEMINATTA') return onceki === 'TAHSIL' ? 'Geri al (teminatta)' : 'Teminata ver';
  if (yeni === 'BANKADA') return onceki === 'TAHSIL' ? 'Geri al (bankada)' : 'Tahsile ver (bankaya)';
  if (yeni === 'BEKLIYOR') return 'Geri al (ödenmedi)';
  // → PORTFOY
  if (onceki === 'CIRO') return 'Ciro iadesi (geri döndü)';
  if (onceki === 'BANKADA') return 'Bankadan geri al';
  if (onceki === 'TEMINATTA') return 'Teminattan çöz';
  if (onceki === 'TAHSIL') return 'Geri al (portföyde)';
  return 'Yeniden portföye al';
}
/** Satırda doğrudan düğme olarak duran işlemler; kalan geçerli işlemler "Diğer işlem…" listesindedir. */
export const ONCELIKLI: Partial<Record<CekDurum, CekDurum[]>> = {
  PORTFOY: ['BANKADA', 'TAHSIL'], BANKADA: ['TAHSIL'], TEMINATTA: ['PORTFOY'], KARSILIKSIZ: ['TAHSIL'], BEKLIYOR: ['ODENDI'],
};
/** Kasa defterine hareket yazan / hareketi iptal eden durumlar. */
export const NAKITLI: CekDurum[] = ['TAHSIL', 'ODENDI'];

/** "vadesine 5 gün" · "bugün" · "3 gün geçti" */
export const kalanYaz = (g: number) => (g > 0 ? `${g} gün kaldı` : g === 0 ? 'bugün' : `${-g} gün geçti`);

/** Para birimi bazındaki toplamdan TL tutarı ve döviz satırı. */
export const tlKurus = <T extends { para: string }>(liste: T[], alan: keyof T) => Number(liste.find((x) => x.para === 'TRY')?.[alan] ?? 0);
export const dovizSatiri = <T extends { para: string }>(liste: T[], alan: keyof T) =>
  liste.filter((x) => x.para !== 'TRY' && Number(x[alan]) !== 0).map((x) => paraYaz(Number(x[alan]), x.para)).join(' · ');

/** Dosyayı oturum jetonuyla indirir (bağlantıya jeton yazılmaz). */
export async function jetonluIndir(yol: string, varsayilanAd: string): Promise<void> {
  const cagir = () => fetch(yol, { headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  let r = await cagir();
  if (r.status === 401) { await api('/api/me').catch(() => undefined); r = await cagir(); }
  if (!r.ok) throw new Error('Dosya hazırlanamadı');
  const ad = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? varsayilanAd;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = ad; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Çek fotoğrafı oturum ister; <img> jeton gönderemediği için önce indirilir. */
export async function gorselAl(ad: string): Promise<string | null> {
  const r = await fetch(`/api/cek/gorsel/${ad}`, { headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  return r.ok ? URL.createObjectURL(await r.blob()) : null;
}

export async function gorselYukle(dosya: File): Promise<string> {
  const f = new FormData();
  f.append('dosya', dosya);
  const r = await fetch('/api/cek/gorsel', { method: 'POST', body: f, headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  const g = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((g as { error?: string }).error ?? 'Görsel yüklenemedi');
  return (g as { gorsel: string }).gorsel;
}
